import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  Settings,
  Calendar,
  Building2,
  User,
  MapPin,
  ArrowUpRight,
  Activity,
  Briefcase,
  Plug,
  Receipt,
  type LucideIcon,
  Bot,
  Palette,
  Inbox,
  FileText,
} from "lucide-react";
import PlanBadge from "@/components/subscription/PlanBadge";
import { Badge } from "@/components/ui/badge";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import type { PlanFeature } from "@/hooks/useSubscriptionConfig";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useBusinessId } from "@/hooks/useBusinessId";
import { ESTIMATE_STATUSES, isAwaitingCustomer, isOpen } from "@/types/estimate-status.generated";

/** Which "you have something waiting" count this item shows, if any. */
type AttentionBadge = "newRequests" | "awaitingBusiness";

interface NavSection {
  labelKey: string;
  items: {
    nameKey: string;
    href: string;
    icon: LucideIcon;
    /** Plan feature required to even SEE this item. Absent = always visible. */
    feature?: PlanFeature;
    badge?: AttentionBadge;
  }[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    labelKey: "overview",
    items: [
      { nameKey: "dashboard", href: "/dashboard", icon: LayoutDashboard },
    ],
  },
  {
    labelKey: "operations",
    items: [
      { nameKey: "jobs", href: "/dashboard/jobs", icon: Briefcase },
      { nameKey: "calendar", href: "/dashboard/calendar", icon: Calendar },
      { nameKey: "customers", href: "/dashboard/customers", icon: Users },
      { nameKey: "technicians", href: "/dashboard/technicians", icon: UserCheck },
      { nameKey: "services", href: "/dashboard/services", icon: Settings },
      { nameKey: "requests", href: "/dashboard/requests", icon: Inbox, feature: "estimates", badge: "newRequests" },
      { nameKey: "estimates", href: "/dashboard/estimates", icon: FileText, feature: "estimates", badge: "awaitingBusiness" },
      { nameKey: "invoices", href: "/dashboard/invoices", icon: Receipt },
      { nameKey: "aiReceptionist", href: "/dashboard/ai", icon: Bot, feature: "ai_receptionist" },
    ],
  },
  {
    labelKey: "assets",
    items: [
      { nameKey: "equipment", href: "/dashboard/equipment", icon: Building2 },
      { nameKey: "liveTracking", href: "/dashboard/location", icon: MapPin },
      { nameKey: "performance", href: "/dashboard/performance", icon: Activity },
    ],
  },
  {
    labelKey: "account",
    items: [
      { nameKey: "branding", href: "/dashboard/branding", icon: Palette, feature: "estimates" },
      { nameKey: "integrations", href: "/dashboard/integrations", icon: Plug },
      { nameKey: "profile", href: "/dashboard/profile", icon: User },
    ],
  },
];

interface SidebarProps {
  onNavigate?: () => void;
  className?: string;
}

// WHO THE BALL IS ON COMES FROM THE STATUS CATALOG, not from a list typed here.
// `estimate-status.generated.ts` is generated from public.estimate_statuses, and
// `awaiting_customer` exists precisely because `changes_requested` is open while
// the ball is ours. The dashboard card splits it the same way; re-deriving it by
// hand is how the two start disagreeing.
const AWAITING_BUSINESS = ESTIMATE_STATUSES.filter((s) => isOpen(s) && !isAwaitingCustomer(s));

const NO_ATTENTION = { newRequests: 0, awaitingBusiness: 0 };

/**
 * Everything with the owner's name on it, in one number per nav item.
 *
 * A lead sat in an inbox with no indicator anywhere in the app, while the
 * feature's own research says a third to a half of jobs go to whoever answers
 * first. The same was true one door along: a customer who asked for a change is
 * waiting on the business, and Estimates carried no indicator at all — the only
 * place that said so was a dashboard card the owner had to be standing on.
 *
 * ONE QUERY, TWO COUNTS. Both are `head: true`, so no rows move, and they share
 * a cache entry and a staleTime rather than each keeping their own.
 *
 * NO POLLING — CLAUDE.md's rule, and at 20K businesses a one-minute interval
 * here would be 1.2M queries an hour to say "still zero".
 */
function useAttentionCounts(businessId: string | null | undefined, enabled: boolean) {
  const { data } = useQuery({
    queryKey: ["sidebar-attention", businessId],
    enabled: !!businessId && enabled,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const [requests, estimates] = await Promise.all([
        // (business_id, status, submitted_at) is indexed by 20260824100500.
        supabase
          .from("estimate_requests")
          .select("*", { count: "exact", head: true })
          .eq("business_id", businessId!)
          .eq("status", "new"),
        // estimates_business_status_idx covers (business_id, status).
        supabase
          .from("estimates")
          .select("*", { count: "exact", head: true })
          .eq("business_id", businessId!)
          .in("status", AWAITING_BUSINESS),
      ]);
      return {
        newRequests: requests.count ?? 0,
        awaitingBusiness: estimates.count ?? 0,
      };
    },
  });
  return data ?? NO_ATTENTION;
}

const SidebarPlanInfo = ({ onNavigate }: { onNavigate?: () => void }) => {
  const { t } = useTranslation("dashboard");
  const { workersLimit, tier, isSubscribed } = usePlanRestrictions();
  const { user } = useAuth();
  // useBusinessId resolves via the user's profile row — correct under RLS
  // even if the user has access to multiple businesses. The previous code
  // here did `.from('businesses').select('id').limit(1).single()` with no
  // user filter, relying entirely on RLS to surface "the right one".
  const businessId = useBusinessId(user);

  const { data: techCount } = useQuery({
    queryKey: ["sidebar-tech-count", businessId],
    enabled: !!businessId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      // Active only. This is compared against the plan's seat limit, and a seat
      // is what an ACTIVE row costs — enforce_technician_seat_limit counts the
      // same way. Counting every row overstated usage against the cap, and now
      // that every business carries an inactive roster row for its owner it
      // would show "1 / 2" to a business with nobody on the schedule.
      const { count } = await supabase
        .from("technicians")
        .select("*", { count: "exact", head: true })
        .eq("business_id", businessId!)
        .eq("is_active", true);
      return count ?? 0;
    },
  });

  if (!isSubscribed || !tier || tier === "internal") return null;

  const count = techCount ?? 0;
  const limit = workersLimit;
  const percentage = limit > 0 ? Math.min((count / limit) * 100, 100) : 0;
  const isNearLimit = percentage >= 80;

  return (
    <div className="px-3 pb-3 border-t border-border pt-3 mt-2">
      <div className="flex items-center justify-between mb-2">
        <PlanBadge />
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t("sidebar.planInfo.techCount", { count, limit })}</span>
          {isNearLimit && <span className="text-amber-600 font-medium">{t("sidebar.planInfo.nearLimit")}</span>}
        </div>
        <Progress
          value={percentage}
          className={`h-1.5 ${isNearLimit ? "[&>div]:bg-amber-500" : "[&>div]:bg-primary"}`}
        />
      </div>
      {tier !== "business" && (
        <Link
          to="/dashboard/billing"
          className="flex items-center gap-1 mt-2 text-xs text-primary hover:underline"
          onClick={onNavigate}
        >
          {t("sidebar.planInfo.upgrade")} <ArrowUpRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
};

export const Sidebar = ({ onNavigate, className }: SidebarProps) => {
  const location = useLocation();
  const { t } = useTranslation("dashboard");
  const { canUse, isLoading: planLoading } = usePlanRestrictions();
  const { user } = useAuth();
  const businessId = useBusinessId(user);
  const attention = useAttentionCounts(businessId, !planLoading && canUse("estimates"));

  // Items carrying a `feature` are hidden outright for businesses without it —
  // not greyed out, not upsold. A business that doesn't have the AI
  // receptionist must not learn it exists from the sidebar. While the plan is
  // still loading we hide too: flashing the item and retracting it would leak
  // the same thing.
  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.feature || (!planLoading && canUse(item.feature))),
  })).filter((section) => section.items.length > 0);

  return (
    <div className={cn("bg-background/95 backdrop-blur-xl border-r border-border", className ?? "fixed inset-y-0 left-0 z-50 w-64")}>
      <div className="flex h-16 items-center px-6 border-b border-border">
        <img src="/workloop-logo.png" alt="Workloop" width="32" height="32" decoding="async" className="h-8 w-8 mr-2" />
        <h1 className="text-xl font-bold text-foreground">Workloop</h1>
      </div>

      <nav className="mt-4 px-3">
        {sections.map((section) => (
          <div key={section.labelKey} className="mb-4">
            <div className="px-3 mb-1.5">
              <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
                {t(`sidebar.sections.${section.labelKey}`)}
              </span>
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = item.href === "/dashboard"
                  ? (location.pathname === "/dashboard" || location.pathname === "/dashboard/")
                  : location.pathname === item.href;
                const count = item.badge ? attention[item.badge] : 0;
                return (
                  <li key={item.nameKey}>
                    <Link
                      to={item.href}
                      onClick={onNavigate}
                      className={cn(
                        "flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
                        isActive
                          ? "bg-primary/10 text-foreground border-l-2 border-primary"
                          : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                      )}
                    >
                      <item.icon className={cn("mr-3 h-4 w-4", isActive && "text-primary")} />
                      <span className="flex-1">{t(`sidebar.nav.${item.nameKey}`)}</span>
                      {item.badge && count > 0 ? (
                        <Badge
                          variant="default"
                          className="ml-2 h-5 min-w-5 justify-center px-1.5 text-xs"
                          aria-label={t(`sidebar.nav.${item.badge}Badge`, { count })}
                        >
                          {count}
                        </Badge>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <SidebarPlanInfo onNavigate={onNavigate} />
    </div>
  );
};
